import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import classNames from 'classnames'
import {
  IconCircleCheckFilled,
  IconCircleMinus,
  IconCircleXFilled,
  IconX,
} from '@tabler/icons-react'

import { Loader } from '@/components/loaders/Loader'
import { Button } from '@/components/buttons/Button'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { fetchCriteriaScoresByMeasureId } from '@/reducers/scorecards/scores.actions'

import { fetchPlaybookById } from '@/reducers/scorecards/scorecards.actions'
import { Table } from '@/components/tables/Table/Table'
import { Pill } from '@/components/pills/Pill'

import { ScorecardCriteriaSummaryTable } from '../components/ScorecardCriteriaSummaryTable'
import { calculatePercentage } from '../CallScoreCarousel/helpers'
import { sortCriteriaByPlaybookSections } from '../helpers/index'

import './measureScoresTable.css'

const criteriaCellClasses = (criteria) => {
  const isDecklist = Object.keys(criteria).includes('decklist_triggered')
  const decklistTriggered = isDecklist && criteria.decklist_triggered
  const countMissed = decklistTriggered || !isDecklist

  return criteria.condition_type === 'AND' && countMissed
    ? {
        span: criteria.hit ? 'hit' : 'missed',
        icon: criteria.hit ? <IconCircleCheckFilled /> : <IconCircleXFilled />,
      }
    : {
        span: criteria.hit ? 'hit' : 'informative',
        icon: criteria.hit ? <IconCircleCheckFilled /> : <IconCircleMinus />,
      }
}

export const createBasicCriteriaCell = (criteria) => {
  const classes = criteriaCellClasses(criteria)

  return (
    <div
      data-testid="criteria-name-cell"
      className={classNames('flex-align-center small-gap', {
        'status-success': classes.span === 'hit',
        'status-critical': classes.span === 'missed',
        'status-informative': classes.span === 'greyed',
      })}
    >
      {classes.icon}
      {criteria.name}
    </div>
  )
}

export const UnknownSectionCriteriaTable = ({ missingCriteria, cellFunction }) => {
  return (
    <div data-testid="criteria-nested-section-table">
      <Table className="measure-scores-table">
        <tbody>
          {missingCriteria.map((criteria) => (
            <tr key={criteria.id}>
              {Object.keys(criteria).includes('hit') ? (
                cellFunction(criteria)
              ) : (
                <td data-testid="criteria-name-cell">{criteria.name}</td>
              )}
              <td className="collapsing">
                <Button disabled xs data-testid="criteria-must-be-present-button">
                  {criteria.must_be_present ? 'Must Be Present' : 'Must Not Be Present'}
                </Button>
              </td>
              <td className="collapsing">
                <Button disabled xs>
                  {criteria.weight.value || criteria.weight}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  )
}

const SECTION_HEADERS = {
  Postcall: 'Post Call',
  Copilot: 'Quality',
  Checklist: 'Checklist',
  Notifications: 'Notifications',
  Deck: 'Deck',
}

export const tablesByPlaybookSection = (playbookSection, sectionLogic) => {
  const sectionHasAndLogic = sectionLogic?.condition_type === 'AND'

  const columns = [
    { label: 'Name', accessor: 'name' },
    { label: 'Present or Not?', accessor: 'must_be_present' },
  ]

  if (sectionHasAndLogic) {
    columns.push({ label: 'Weight', accessor: 'weight' })
  }

  return (
    <ScorecardCriteriaSummaryTable
      key={playbookSection.sectionName}
      columns={columns}
      rows={playbookSection.criteria}
      header={SECTION_HEADERS[playbookSection.sectionName]}
    />
  )
}

export const CriteriaScoresTable = ({ measure, setOpen }) => {
  const { data, loading } = useSelector((state) => state.scorecards)

  if (loading.criteriaScoresByMeasure || loading.targetPlaybook) {
    return <Loader centered />
  }

  if (isEmpty(data.criteriaScoresByMeasure) || isEmpty(data.targetPlaybook)) {
    return null
  }

  const criteriaByPlaybookSection = sortCriteriaByPlaybookSections(
    data.criteriaScoresByMeasure,
    data.targetPlaybook.body
  )

  return (
    <div data-testid="criteria-scores-summary__popup-card">
      <div className="flex-space-between mb">
        <div className="flex-align-center small-gap">
          <h3 className="no-margin">{measure.name}</h3>
          <AndOrLogicLabel measureScore={data.criteriaScoresByMeasure || {}} />
          <AutoFailZeroLabel measureScore={measure} />
        </div>
        <Button
          basic
          square
          small
          onClick={() => setOpen(false)}
          data-testid="basic-modal-close-button"
        >
          <IconX />
        </Button>
      </div>
      <div className="criteria-scores-summary__content-container">
        {criteriaByPlaybookSection.map((playbookSection) =>
          tablesByPlaybookSection(playbookSection, data.criteriaScoresByMeasure)
        )}
      </div>
    </div>
  )
}

export const CriteriaScorePopup = ({ measureScore }) => {
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false)
  const { data } = useSelector((state) => state.scorecards)
  useEffect(() => {
    if (open) {
      if (data.currentScore.id && measureScore.id) {
        dispatch(fetchCriteriaScoresByMeasureId(data.currentScore.id, measureScore.id))
        dispatch(fetchPlaybookById(data.currentScore.config_id))
      }
    }
  }, [open])

  if (!measureScore) {
    return null
  }

  return (
    <td>
      <InteractiveTooltip
        clickable
        open={open}
        setOpen={setOpen}
        placement="top"
        theme="light"
        className="criteria-scores-summary__popup"
        content={<CriteriaScoresTable measure={measureScore} setOpen={setOpen} />}
      >
        <a data-testid="criteria-scores-summary__popup-trigger" className="table-link">
          {measureScore.name}
        </a>
      </InteractiveTooltip>
    </td>
  )
}

export const AutoFailZeroLabel = ({ measureScore }) => {
  if (!measureScore.auto_failed && !measureScore.auto_zeroed) return null

  if (measureScore.auto_failed) {
    return (
      <Pill small critical data-testid="autofail-badge-measure">
        Auto-fail
      </Pill>
    )
  }

  return (
    <Pill small caution data-testid="autozero-badge-measure">
      Auto-zero
    </Pill>
  )
}

export const AndOrLogicLabel = ({ measureScore }) => {
  if (!measureScore || !measureScore.condition_type) return null

  if (measureScore.condition_type === 'AND') {
    return (
      <Pill small emphasized data-testid="and-logic-badge">
        AND Logic
      </Pill>
    )
  }

  if (measureScore.condition_type === 'OR') {
    return (
      <Pill small emphasized data-testid="or-logic-badge">
        OR Logic
      </Pill>
    )
  }

  return null
}

export const MeasureScoresTable = ({ measureScores = [], canViewCriteria = false }) => {
  return (
    <Table celled className="measure-scores-table" data-testid="measure-scores-table">
      <tbody>
        {measureScores.map((measureScore) => {
          return (
            <tr key={measureScore.id}>
              {canViewCriteria ? (
                <CriteriaScorePopup measureScore={measureScore} />
              ) : (
                <td>{measureScore.name}</td>
              )}
              <td className="text-right">
                {measureScore.auto_failed || measureScore.auto_zeroed ? (
                  <AutoFailZeroLabel measureScore={measureScore} />
                ) : (
                  `${
                    measureScore.percentage_score ||
                    calculatePercentage(measureScore.aggregate_score, measureScore.possible_score)
                  }%`
                )}
              </td>
            </tr>
          )
        })}
      </tbody>
    </Table>
  )
}
